import React, { useEffect, useRef, useState } from "react";
import { FaChevronDown, FaGripHorizontal } from "react-icons/fa";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import LogoSection from "./LogoSection";
import MegaMenu from "./MegaMenu";

import { HOME_NAV_LINKS } from "../data/navigationData";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useScrollState } from "../hooks/useScrollState";
import { scrollToHomeSection } from "../utils/navigationUtils";

import HamburgerButton from "../ui/HamburgerButton";

const LinkNav = ({ user, settings }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);

  const overlayRef = useRef(null);
  const scrolled = useScrollState(8);

  useBodyScrollLock(megaMenuOpen);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      setMobileOpen(false);
      setMegaMenuOpen(false);
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaMenuOpen(false);
  }, [location.pathname]);

  const handleHomeNavigate = (sectionId) => {
    scrollToHomeSection({
      sectionId,
      navigate,
      location,
    });

    setMobileOpen(false);
    setMegaMenuOpen(false);
  };

  const toggleMegaMenu = () => {
    setMegaMenuOpen((current) => !current);
    setMobileOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileOpen((current) => !current);
    setMegaMenuOpen(false);
  };

  return (
    <nav
      className={`w-full border-b transition-all duration-300 ${
        scrolled
          ? "border-slate-200/80 bg-white/90 shadow-lg shadow-slate-950/5 backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/90"
          : "border-white/40 bg-white/80 backdrop-blur-md dark:border-white/10 dark:bg-slate-900/80"
      }`}
    >
      <div className="mx-auto w-full max-w-[1380px] px-2 sm:px-4 lg:px-6">
        <div className="flex h-16 min-w-0 items-center justify-between gap-2 sm:h-[76px] sm:gap-3">
          <NavLink
            to="/"
            aria-label="Accueil MonAppliTournoi"
            className="min-w-0 flex-1 lg:flex-none"
          >
            <LogoSection settings={settings} />
          </NavLink>

          <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
            <ul className="flex min-w-0 items-center gap-1 rounded-full border border-slate-200 bg-slate-50/80 p-1 dark:border-white/10 dark:bg-white/5">
              {HOME_NAV_LINKS.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => handleHomeNavigate(item.id)}
                    className="rounded-full px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-white hover:text-orange-600 hover:shadow-sm dark:text-slate-200 dark:hover:bg-white/10 dark:hover:text-orange-300"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={toggleMegaMenu}
              aria-expanded={megaMenuOpen}
              className="
                group inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-2xl
                bg-gradient-to-r from-orange-500 to-orange-600 px-3 py-2 text-sm font-bold text-white
                shadow-lg shadow-orange-500/20 transition hover:-translate-y-0.5 hover:shadow-orange-500/30
                focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2
                dark:focus-visible:ring-offset-slate-950
                sm:min-h-11 sm:gap-2 sm:px-4 sm:py-2.5
              "
            >
              <FaGripHorizontal className="h-4 w-4 shrink-0" />
              <span className="hidden sm:inline">Application</span>
              <FaChevronDown
                className={`h-3 w-3 shrink-0 transition ${
                  megaMenuOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            <HamburgerButton open={mobileOpen} onClick={toggleMobileMenu} />
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden">
          <div className="border-t border-slate-200 bg-white/95 px-2 py-3 shadow-xl backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/95 sm:px-4">
            <div className="mx-auto w-full max-w-[1380px]">
              <div className="grid gap-2">
                {HOME_NAV_LINKS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => handleHomeNavigate(item.id)}
                    className="
                      flex min-h-12 w-full items-center justify-between rounded-2xl border border-slate-200
                      bg-slate-50 px-4 text-left text-sm font-bold text-slate-800 transition
                      hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700
                      dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:border-orange-400/40 dark:hover:bg-orange-400/10
                    "
                  >
                    <span className="truncate">{item.label}</span>
                    <span className="shrink-0 text-orange-500">→</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {megaMenuOpen && (
        <div
          ref={overlayRef}
          className="
      fixed inset-0 z-[99999] flex h-[100dvh] w-screen overflow-hidden
      bg-slate-950/75 backdrop-blur-sm
      lg:items-start lg:justify-center lg:p-4
    "
          onMouseDown={(event) => {
            if (event.target === overlayRef.current) {
              setMegaMenuOpen(false);
            }
          }}
        >
          <MegaMenu
            roleId={user?.role_id}
            onClose={() => setMegaMenuOpen(false)}
          />
        </div>
      )}
    </nav>
  );
};

export default LinkNav;
